// Shared SVG construction. Motifs are drawn, never described: nothing in this
// directory writes markup, and no model-authored string ever reaches the DOM —
// a motif carries only a name, an intensity and a position.
//
// The vocabulary is deliberately Bauhaus: circle, square, triangle, bold flat
// planes, heavy/thin stroke contrast, asymmetric balance, and two colours from
// the palette overlapping so the translucency does the mixing. Atmosphere comes
// from a soft bloom behind the geometry, not from gradients inside it.

const NS = 'http://www.w3.org/2000/svg';

// A wide drawing box gives the composition room to be asymmetric. Motifs are
// laid out in these coordinates and scaled by the container, so a phone and a
// wide desktop get the same composition.
export const BOX = { width: 480, height: 260 };

let idCounter = 0;
/** Gradient and filter ids must be unique across every motif on the page. */
export function nextId(prefix) {
  idCounter += 1;
  return `atm-${prefix}-${idCounter}`;
}

export function shape(tag, attributes = {}) {
  const node = document.createElementNS(NS, tag);
  Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, String(value)));
  return node;
}

export function svgRoot() {
  const svg = shape('svg', {
    viewBox: `0 0 ${BOX.width} ${BOX.height}`,
    preserveAspectRatio: 'xMidYMid meet',
    focusable: 'false',
    // Purely atmospheric: it carries no information a reader could miss.
    'aria-hidden': 'true',
  });
  return svg;
}

/**
 * A soft bloom behind the geometry. This is what stops a composition reading as
 * a diagram: the hard Bauhaus shapes sit on a field of colour rather than on
 * bare paper.
 */
export function bloom(svg, { cx, cy, r, colour = 'currentColor', opacity = 0.5 }) {
  const id = nextId('bloom');
  const defs = shape('defs');
  const filter = shape('filter', { id, x: '-50%', y: '-50%', width: '200%', height: '200%' });
  filter.appendChild(shape('feGaussianBlur', { stdDeviation: Math.round(r / 2.6) }));
  defs.appendChild(filter);
  svg.appendChild(defs);
  const node = shape('circle', { cx, cy, r, fill: colour, filter: `url(#${id})`, opacity });
  node.classList.add('motif-bloom');
  svg.appendChild(node);
  return node;
}

/**
 * Roughens every edge inside a group. Perfect geometry reads as a chart; a little
 * displacement reads as something made.
 */
export function organic(svg, { strength = 3, frequency = 0.02, seed = 3 } = {}) {
  const id = nextId('organic');
  const defs = shape('defs');
  const filter = shape('filter', { id, x: '-20%', y: '-20%', width: '140%', height: '140%' });
  filter.appendChild(shape('feTurbulence', {
    type: 'fractalNoise', baseFrequency: frequency, numOctaves: 2, seed, result: 'noise',
  }));
  filter.appendChild(shape('feDisplacementMap', {
    in: 'SourceGraphic', in2: 'noise', scale: strength, xChannelSelector: 'R', yChannelSelector: 'G',
  }));
  defs.appendChild(filter);
  svg.appendChild(defs);
  const group = shape('g', { filter: `url(#${id})` });
  svg.appendChild(group);
  return group;
}

/** A flat colour plane — the Bauhaus primitive. Arrives by scaling into place. */
export function plane(tag, attributes = {}, { order = 0, from = 'up' } = {}) {
  const node = shape(tag, { fill: 'currentColor', ...attributes });
  node.classList.add('motif-plane');
  node.dataset.from = from;
  node.style.setProperty('--motif-order', String(order));
  return node;
}

/**
 * A stroked line or path that draws itself in. The dash length is set from the
 * geometry so the reveal reads as drawing rather than fading.
 */
export function stroke(tag, attributes = {}, { length = 400, order = 0 } = {}) {
  const node = shape(tag, { fill: 'none', 'stroke-linecap': 'butt', ...attributes });
  node.classList.add('motif-stroke');
  node.style.setProperty('--motif-length', String(length));
  node.style.setProperty('--motif-order', String(order));
  return node;
}

/** A filled mark that arrives by scaling up rather than being drawn. */
export function mark(tag, attributes = {}, { order = 0 } = {}) {
  const node = shape(tag, { fill: 'currentColor', ...attributes });
  node.classList.add('motif-mark');
  node.style.setProperty('--motif-order', String(order));
  return node;
}

/** Evenly spaced positions across the box, inset from both edges. */
export function spread(count, inset = 40) {
  if (count <= 1) return [BOX.width / 2];
  const usable = BOX.width - inset * 2;
  return Array.from({ length: count }, (_, index) => inset + (usable * index) / (count - 1));
}

/** Intensity 1–3 scales how much of the form is present, never its size. */
export function elementsFor(intensity, low, high) {
  const steps = [low, Math.round((low + high) / 2), high];
  return steps[Math.max(0, Math.min(2, Number(intensity) - 1))] ?? steps[1];
}

/** The palette's warm hue, used as the second colour in a two-colour composition. */
export const ACCENT = 'var(--atm-warm)';
export const COOL = 'var(--atm-cool)';

/** An equilateral-ish triangle as a path, the third Bauhaus primitive. */
export function trianglePath(cx, cy, size, rotation = 0) {
  const points = [0, 120, 240].map((angle) => {
    const radians = ((angle + rotation) * Math.PI) / 180;
    return `${(cx + Math.sin(radians) * size).toFixed(1)} ${(cy - Math.cos(radians) * size).toFixed(1)}`;
  });
  return `M${points.join(' L')} Z`;
}
